/**
 * Modal Rapprochement Bancaire V2 — miroir strict du modal Wave.
 * Spec : /app/memory/SPEC_BANK_RECONCILIATION_V2.md (07/07/2026)
 *
 * Fonctionnalités :
 *  - Recherche unifiée : contract_invoices + opérations spot
 *  - Filtre segmenté Toutes / Contrat / Opération
 *  - Toggle "Inclure factures soldées"
 *  - Allocations multi (paiements groupés)
 *  - Historique paiements expandable par ligne
 *  - Justification d'écart obligatoire si Σ allocations < montant tx
 *  - Motif obligatoire
 *  - Crée des Payments RÉELS (pas symboliques)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, CheckCircle2, ChevronDown, ChevronRight, History, Landmark,
  Loader2, Search, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fxof = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR')} XOF`);
const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString('fr-FR');
  } catch (e) { return String(v); }
};

// Motifs d'écart figés (spec §5.2)
const GAP_JUSTIFICATION_OPTIONS = [
  { value: 'frais', label: 'Frais bancaires' },
  { value: 'retenue', label: 'Retenue fiscale' },
  { value: 'remise', label: 'Remise commerciale' },
  { value: 'arrondi', label: 'Arrondi' },
  { value: 'erreur_bancaire', label: 'Erreur bancaire' },
  { value: 'compensation', label: 'Compensation' },
  { value: 'autre', label: 'Autre (préciser)' },
];

// ────────────────────────────────────────────────────────────────────
// SOUS-COMPOSANT : Historique des paiements
// ────────────────────────────────────────────────────────────────────
function InvoicePaymentsHistory({ sourceType, invoiceId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await axios.get(`${API}/bank/invoice-payments/${sourceType}/${invoiceId}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Impossible de charger les paiements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceType, invoiceId]);

  if (loading) {
    return (
      <div className="pl-8 py-2 text-[11px] text-zinc-500">
        <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Chargement...
      </div>
    );
  }
  if (error) return <div className="pl-8 py-2 text-[11px] text-rose-600">{error}</div>;
  if (!data || !data.payments?.length) {
    return <div className="pl-8 py-2 text-[11px] text-zinc-500 italic">Aucun paiement encore enregistré.</div>;
  }

  return (
    <div className="pl-8 pr-3 py-2 space-y-1 bg-white/60" data-testid="bank-payment-history">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold mb-1 flex items-center gap-1">
        <History className="h-3 w-3" /> Historique ({data.payments.length})
      </div>
      {data.payments.map((p, i) => (
        <div key={p.id || i}
          className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${
            p.voided ? 'bg-rose-50/60 line-through text-rose-500' : 'bg-blue-50/40'
          }`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
            p.source === 'wave' ? 'bg-emerald-500' : p.source === 'bank' ? 'bg-blue-500' : 'bg-zinc-400'
          }`} />
          <span className="text-zinc-600 font-mono min-w-[85px]">{fmtDate(p.date)}</span>
          <span className="font-semibold text-zinc-800">{fxof(p.amount)}</span>
          <span className="text-zinc-500 text-[10px] uppercase">{p.method || '—'}</span>
          {p.reference && (
            <span className="text-zinc-400 font-mono text-[10px] truncate max-w-[140px]" title={p.reference}>
              {p.reference}
            </span>
          )}
          {p.source && (
            <span className={`ml-auto text-[9px] px-1 py-0.5 rounded ${
              p.source === 'wave' ? 'bg-emerald-100 text-emerald-700' :
              p.source === 'bank' ? 'bg-blue-100 text-blue-700' :
              'bg-zinc-100 text-zinc-600'
            }`}>
              {p.source}
            </span>
          )}
          {p.voided && <span className="text-[9px] text-rose-600 font-semibold">ANNULÉ</span>}
        </div>
      ))}
      <div className="flex items-center justify-between pt-1 border-t border-zinc-200 mt-1 text-[11px]">
        <span className="text-zinc-500">Total encaissé</span>
        <span className="font-semibold text-blue-700">
          {fxof(data.amountPaid)} / {fxof(data.invoiceAmount)}
        </span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// MODAL PRINCIPAL — Rapprochement bancaire V2
// ────────────────────────────────────────────────────────────────────
const ReconciliationDialog = ({ open, onClose, tx, onDone }) => {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includePaid, setIncludePaid] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [allocations, setAllocations] = useState([]);
  const [reason, setReason] = useState('');
  const [gapReason, setGapReason] = useState('');
  const [gapReasonCustom, setGapReasonCustom] = useState('');
  const [notifyClient, setNotifyClient] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!open) {
      setQuery(''); setCandidates([]); setAllocations([]);
      setReason(''); setGapReason(''); setGapReasonCustom('');
      setNotifyClient(true); setIncludePaid(false); setTypeFilter('all');
      setExpandedInvoices(new Set());
    }
  }, [open]);

  const searchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bank/searchable-invoices`, {
        params: { q: query.trim(), includePaid, type: typeFilter, limit: 30 },
      });
      setCandidates(data?.results || []);
    } catch (e) {
      toast({
        title: 'Recherche impossible',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally { setLoading(false); }
  }, [query, includePaid, typeFilter]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(searchInvoices, 250);
    return () => clearTimeout(t);
  }, [open, searchInvoices]);

  const addAllocation = (inv) => {
    if (allocations.some(a => a.invoiceId === inv.id)) return;
    const remaining = (tx?.amount || 0) - allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
    const proposed = Math.min(inv.amountDue || 0, Math.max(0, remaining));
    setAllocations([...allocations, {
      invoiceId: inv.id, sourceType: inv.sourceType,
      invoiceNumber: inv.invoiceNumber, companyName: inv.companyName,
      operationNumber: inv.operationNumber,
      amount: proposed, amountDue: inv.amountDue || 0,
    }]);
  };

  const updateAmount = (idx, val) => {
    const nv = [...allocations];
    nv[idx].amount = Math.max(0, Math.min(Number(val) || 0, nv[idx].amountDue));
    setAllocations(nv);
  };

  const removeAllocation = (idx) => setAllocations(allocations.filter((_, i) => i !== idx));

  const totalAllocated = allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  const txAmount = tx?.amount || 0;
  const overAllocated = totalAllocated > txAmount;
  const gap = txAmount - totalAllocated;
  const needsGapJustification = gap > 0 && allocations.length > 0;

  const submit = async () => {
    if (!allocations.length) { toast({ title: 'Ajoutez au moins une facture', variant: 'destructive' }); return; }
    if (overAllocated) { toast({ title: 'Somme > montant tx', variant: 'destructive' }); return; }
    if (reason.trim().length < 3) { toast({ title: 'Motif requis (min 3 car.)', variant: 'destructive' }); return; }
    if (needsGapJustification && !gapReason) {
      toast({ title: 'Écart non justifié', description: 'Sélectionnez un motif d\'écart.', variant: 'destructive' });
      return;
    }
    const finalGapJustif = gapReason === 'autre' ? gapReasonCustom.trim() : gapReason;
    if (needsGapJustification && gapReason === 'autre' && finalGapJustif.length < 3) {
      toast({ title: 'Précisez le motif "Autre"', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bank/transactions/${tx.id}/reconcile-invoices`, {
        allocations: allocations.map(a => ({
          invoiceId: a.invoiceId,
          amount: Math.round(Number(a.amount)),
          sourceType: a.sourceType || 'contract',
        })),
        reason: reason.trim(),
        gap_justification: needsGapJustification ? finalGapJustif : null,
        notifyClient,
      });
      toast({
        title: 'Rapprochement effectué ✅',
        description: `${data.payments?.length || 0} paiement(s) créé(s) — ${fxof(data.totalAllocated)}`,
      });
      onDone?.(); onClose();
    } catch (e) {
      toast({
        title: 'Échec rapprochement',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally { setSubmitting(false); }
  };

  if (!tx) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bank-reconcile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-blue-600" /> Rapprocher cette transaction bancaire
          </DialogTitle>
          <DialogDescription>
            <strong>{tx.label}</strong> · Réf&nbsp;: {tx.reference || '—'} · Montant&nbsp;: <strong>{fxof(tx.amount)}</strong> · Direction&nbsp;: {tx.direction}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Barre de recherche */}
          <div>
            <Label className="text-xs font-medium">Rechercher (n° opération, n° facture, ou société)</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-zinc-400" />
              <Input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="ex: BAO-OP-2026-… · FACT-2026-0122 · INV-2026 · SOGENA"
                className="pl-7 h-9 text-sm"
                data-testid="bank-reconcile-search"
                autoFocus
              />
            </div>
          </div>

          {/* Filtres */}
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <span className="text-zinc-500">Type&nbsp;:</span>
              {[
                { v: 'all', label: 'Toutes' },
                { v: 'contract', label: '📄 Contrat' },
                { v: 'operation', label: '⚡ Opération' },
              ].map(o => (
                <button
                  key={o.v} type="button"
                  onClick={() => setTypeFilter(o.v)}
                  className={`px-2 py-0.5 rounded border transition ${
                    typeFilter === o.v
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid={`bank-reconcile-type-${o.v}`}
                >{o.label}</button>
              ))}
            </div>
            <label className="flex items-center gap-1 cursor-pointer ml-auto">
              <input type="checkbox" checked={includePaid}
                onChange={(e) => setIncludePaid(e.target.checked)}
                data-testid="bank-reconcile-include-paid" />
              <span className="text-zinc-600">Inclure factures soldées</span>
            </label>
          </div>

          {/* Résultats */}
          {loading && (
            <div className="text-xs text-zinc-500 py-4 text-center">
              <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Recherche...
            </div>
          )}

          {!loading && candidates.length === 0 && (
            <div className="text-xs text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-md">
              Aucune facture. Essayez un autre critère ou activez « Inclure factures soldées ».
            </div>
          )}

          {!loading && candidates.length > 0 && (
            <div className="border border-zinc-200 rounded-md max-h-[320px] overflow-y-auto divide-y">
              {candidates.map(inv => {
                const already = allocations.some(a => a.invoiceId === inv.id);
                const isSpot = inv.sourceType === 'operation';
                const isExpanded = expandedInvoices.has(inv.id);
                const hasHistory = (inv.amountPaid || 0) > 0;
                return (
                  <div key={`${inv.sourceType}-${inv.id}`}
                    className={`hover:bg-zinc-50 transition ${already ? 'opacity-40' : ''} ${inv.isOverdue ? 'border-l-4 border-l-rose-500' : ''}`}
                    data-testid={`bank-candidate-${inv.id}`}>
                    <div className="px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                              isSpot ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                     : 'bg-blue-100 text-blue-700 border border-blue-200'
                            }`}>{isSpot ? '⚡ OPÉRATION' : '📄 CONTRAT'}</span>
                            <span className="font-semibold text-sm">{inv.invoiceNumber}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                              inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              inv.isOverdue ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              'bg-zinc-50 text-zinc-700 border-zinc-200'
                            }`}>{inv.status}{inv.isOverdue && ` · +${inv.daysOverdue}j`}</span>
                          </div>
                          <div className="text-xs text-zinc-700 font-medium">{inv.companyName}</div>
                          {isSpot && inv.operationNumber && (
                            <div className="text-[11px] text-zinc-500">
                              Opération&nbsp;: <span className="font-mono">{inv.operationNumber}</span>
                              {inv.route && <> · {inv.route}</>}
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-[11px] pt-0.5">
                            <span className="text-zinc-500">TTC&nbsp;: <span className="font-medium text-zinc-700">{fxof(inv.montantTotalTTC)}</span></span>
                            {inv.amountPaid > 0 && (
                              <span className="text-zinc-500">Payé&nbsp;: <span className="text-emerald-700">{fxof(inv.amountPaid)}</span></span>
                            )}
                            <span className="text-zinc-500">Reste dû&nbsp;: <strong className={inv.amountDue > 0 ? 'text-rose-700' : 'text-emerald-700'}>{fxof(inv.amountDue)}</strong></span>
                            {hasHistory && (
                              <button type="button" onClick={() => toggleExpand(inv.id)}
                                className="ml-auto flex items-center gap-1 text-[10px] text-blue-700 hover:text-blue-900 hover:underline"
                                data-testid={`bank-toggle-history-${inv.id}`}>
                                <History className="h-3 w-3" />
                                {isExpanded ? 'Masquer' : 'Voir'} paiements
                                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                            {inv.issuedAt && <span>Émise le {fmtDate(inv.issuedAt)}</span>}
                            {inv.dueDate && <span>· Échéance {fmtDate(inv.dueDate)}</span>}
                          </div>
                        </div>
                        <Button size="sm" variant="outline"
                          onClick={() => addAllocation(inv)}
                          disabled={already || inv.amountDue <= 0}
                          data-testid={`bank-reconcile-add-${inv.id}`}
                          className="shrink-0">
                          {already ? '✓ Ajoutée' : 'Rapprocher →'}
                        </Button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-zinc-100 bg-zinc-50/40">
                        <InvoicePaymentsHistory sourceType={inv.sourceType} invoiceId={inv.id} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Allocations */}
          {allocations.length > 0 && (
            <div className="border border-blue-200 rounded-md bg-blue-50/50 p-2 space-y-1.5">
              <div className="text-xs font-medium text-blue-800 mb-1">
                Allocations ({allocations.length})
              </div>
              {allocations.map((a, idx) => (
                <div key={a.invoiceId} className="flex items-center gap-2 text-xs">
                  <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${
                    a.sourceType === 'operation' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                  }`}>{a.sourceType === 'operation' ? '⚡' : '📄'}</span>
                  <div className="flex-1 min-w-0 truncate">
                    {a.invoiceNumber} · {a.companyName}
                    {a.operationNumber && <span className="text-zinc-500"> ({a.operationNumber})</span>}
                    <span className="text-zinc-500"> · dû {fxof(a.amountDue)}</span>
                  </div>
                  <Input type="number" value={a.amount} onChange={(e) => updateAmount(idx, e.target.value)}
                    className="h-7 w-28 text-right text-xs" min={0} max={a.amountDue}
                    data-testid={`bank-alloc-amount-${idx}`} />
                  <span className="text-zinc-500">XOF</span>
                  <Button size="icon" variant="ghost" onClick={() => removeAllocation(idx)} className="h-6 w-6">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <div className={`flex items-center justify-between pt-1 border-t border-blue-200 mt-1 text-xs ${overAllocated ? 'text-rose-700' : 'text-blue-800'}`}>
                <span>Total alloué</span>
                <strong>{fxof(totalAllocated)} / {fxof(txAmount)}</strong>
              </div>
              {gap > 0 && !overAllocated && (
                <div className="text-[11px] text-amber-700 font-medium">
                  Écart : {fxof(gap)} — justification obligatoire ci-dessous
                </div>
              )}
              {overAllocated && <div className="text-[11px] text-rose-700 font-medium">Dépassement du montant tx.</div>}
            </div>
          )}

          {/* Justification d'écart (obligatoire si gap > 0) */}
          {needsGapJustification && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-2 space-y-2" data-testid="bank-gap-justification">
              <Label className="text-xs font-semibold text-amber-900">
                Justification de l&apos;écart de {fxof(gap)} *
              </Label>
              <select
                value={gapReason}
                onChange={(e) => setGapReason(e.target.value)}
                className="w-full text-xs h-8 border border-amber-300 rounded px-2 bg-white"
                data-testid="bank-gap-reason-select"
              >
                <option value="">— Sélectionnez un motif —</option>
                {GAP_JUSTIFICATION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {gapReason === 'autre' && (
                <Input
                  value={gapReasonCustom}
                  onChange={(e) => setGapReasonCustom(e.target.value)}
                  placeholder="Précisez le motif..."
                  className="text-xs h-8"
                  data-testid="bank-gap-reason-custom"
                />
              )}
            </div>
          )}

          <div>
            <Label className="text-xs font-medium">Motif du rapprochement *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Virement client identifié par appel / cohérence montant + libellé / références transmises..."
              className="min-h-[60px] text-xs mt-1" data-testid="bank-reconcile-reason" />
          </div>

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={notifyClient} onChange={(e) => setNotifyClient(e.target.checked)}
              data-testid="bank-reconcile-notify" />
            <span>Notifier le client (in-app)</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={submit}
            disabled={submitting || !allocations.length || overAllocated || reason.trim().length < 3 || (needsGapJustification && !gapReason)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="bank-reconcile-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirmer le rapprochement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconciliationDialog;
